import React from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { useProjectActivityContext } from 'components/contexts/ProjectActivityContext';
import ProjectActivityBlankNotice from 'components/projectActivity/components/ProjectActivityBlankNotice';
import ProjectActivityTable from 'components/projectActivity/components/ProjectActivityTable';

import { collateMessagesAndDispatches } from 'components/helpers/resources/messages';

export default function ProjectActivityTab() {
  const { isProjectIncomplete, projectId } = useProjectActivityContext();

  const {
    data: histories,
    fetchNextPage: fetchNextHistoriesPage,
    hasNextPage: hasNextHistoriesPage,
    isFetchingNextPage: isFetchingNextHistoriesPage,
  } = useInfiniteQuery({
    queryKey: ['projectHistories', projectId],
    queryFn: async ({ pageParam = 1 }) =>
      (await getProjectHistories(projectId, pageParam)).data,
    getNextPageParam: (lastPage, _allPages) =>
      lastPage.meta.isLastPage ?
        undefined
      : parseInt(lastPage.meta.loadedPageNo, 10) + 1,
  });

  const getProjectHistories = (projectId, pageParam) =>
    axios.get(`/projects/${projectId}/histories`, {
      params: { page_no: pageParam },
    });

  const allIncludedData = histories?.pages?.flatMap((page) => page.included);

  const formatMessageInfo = (histories) => {
    if (!histories) return {};

    return collateMessagesAndDispatches(allIncludedData).reduce(
      (accumulator, object) => {
        const id = Object.keys(object)[0];
        accumulator[id] = object[id];
        return accumulator;
      },
      {},
    );
  };

  const formatHistories = (histories) => {
    if (!histories) return [];

    return histories.pages
      .flatMap((page) => page.data)
      .map((history) => {
        const personnel = allIncludedData.find(
          (includedData) =>
            includedData.id === history.relationships?.personnel?.data?.id &&
            includedData.type === 'personnel',
        );

        const user = allIncludedData.find(
          (includedData) =>
            includedData.id === history.relationships?.user?.data?.id &&
            includedData.type === 'user',
        );

        const profilePhoto = allIncludedData.find((includedData) => {
          if (personnel) {
            return (
              includedData?.relationships?.personnel?.data?.id ===
                personnel?.id && includedData.type === 'profilePhoto'
            );
          }
          return (
            includedData?.relationships?.personnel?.data?.id ===
              user?.relationships?.personnel?.data?.id &&
            includedData.type === 'profilePhoto'
          );
        });

        return {
          id: history.id,
          reason: history.attributes.reason,
          createdAt: history.attributes.createdAt,
          displayNumber: history.attributes.displayNumber,
          nadminUser: history.attributes.nadminUserShortName,
          messageId:
            history.relationships.message.data &&
            history.relationships.message.data.id,
          versionId:
            history.relationships.version.data &&
            parseInt(history.relationships.version.data.id, 10),
          // set either personnel or user for the initials if a photo isn't available
          user: (personnel || user)?.attributes,
          profilePhoto: profilePhoto?.links,
          isApiActivity: history.attributes?.context?.includes('api'),
          isSimproActivity: history.attributes?.context?.includes('simpro'),
        };
      });
  };

  return isProjectIncomplete ?
      <ProjectActivityBlankNotice />
    : <ProjectActivityTable
        hasMore={hasNextHistoriesPage}
        isActive={!isFetchingNextHistoriesPage}
        loadMore={fetchNextHistoriesPage}
        messageInfo={formatMessageInfo(histories)}
        projectHistories={formatHistories(histories)}
      />;
}
